import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { PageHero, SectionTitle } from "@/components/Shared";
import { Reveal } from "@/components/Reveal";
import { MapPin, Phone, Mail, Clock, Send, Loader2 } from "lucide-react";
import { CONTACT, IMAGES } from "@/data/site";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { name: "", email: "", phone: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  const infoCards = [
    { icon: MapPin, t: "Location", v: CONTACT.address },
    { icon: Phone, t: "Call Us", v: CONTACT.phone, href: `tel:${CONTACT.phone}` },
    { icon: Mail, t: "Email", v: CONTACT.email, href: `mailto:${CONTACT.email}` },
  ];

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="CONTACT"
        title="Find The Track"
        subtitle="Questions, directions or an enquiry? Reach out — or just arrive and drive during opening hours."
        image={IMAGES.trackDrone}
        testid="contact-hero"
      />

      <section className="bg-[#050505] py-24 md:py-32">
        <div className="mx-auto grid max-w-[1400px] gap-14 px-5 md:grid-cols-2 md:px-10">
          {/* Info + hours + map */}
          <div>
            <SectionTitle eyebrow="VISIT US" title="Get In Touch" />
            <div className="mt-8 grid gap-4 sm:grid-cols-3">
              {infoCards.map((c) => (
                <Reveal key={c.t}>
                  <div className="h-full rounded-xl border border-white/10 bg-obsidian p-5">
                    <c.icon className="h-6 w-6 text-kbcyan" />
                    <h4 className="mt-3 font-display text-lg tracking-wide text-white">{c.t}</h4>
                    {c.href ? (
                      <a href={c.href} className="mt-1 block text-sm text-white/55 hover:text-kbyellow">{c.v}</a>
                    ) : (
                      <p className="mt-1 text-sm text-white/55">{c.v}</p>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>

            <div className="mt-6 rounded-xl border border-white/10 bg-obsidian p-6">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-kbyellow" />
                <h4 className="font-display text-2xl tracking-wide text-white">Opening Hours</h4>
              </div>
              <ul className="mt-4 divide-y divide-white/10">
                {CONTACT.hours.map((h) => (
                  <li key={h.day} className="flex justify-between py-2.5 text-sm">
                    <span className="text-white/60">{h.day}</span>
                    <span className="font-medium text-white">{h.time}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6 overflow-hidden rounded-xl border border-white/10">
              <iframe
                title="Karting Bahamas location"
                src={CONTACT.mapEmbed}
                data-testid="contact-map"
                className="h-64 w-full grayscale invert-[0.9]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Form */}
          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="contact-form" className="rounded-xl border border-white/10 bg-obsidian p-7 md:p-9">
              <h3 className="font-display text-4xl tracking-wide text-white">Send A Message</h3>
              <p className="mt-2 text-sm text-white/50">We typically reply within one business day.</p>
              <div className="mt-7 grid gap-5">
                <div>
                  <Label className="text-white/70">Full Name *</Label>
                  <Input data-testid="contact-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" className="mt-2 border-white/15 bg-white/5 text-white placeholder:text-white/30" />
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <Label className="text-white/70">Email *</Label>
                    <Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" className="mt-2 border-white/15 bg-white/5 text-white placeholder:text-white/30" />
                  </div>
                  <div>
                    <Label className="text-white/70">Phone</Label>
                    <Input data-testid="contact-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Optional" className="mt-2 border-white/15 bg-white/5 text-white placeholder:text-white/30" />
                  </div>
                </div>
                <div>
                  <Label className="text-white/70">Subject</Label>
                  <Input data-testid="contact-subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} placeholder="What's this about?" className="mt-2 border-white/15 bg-white/5 text-white placeholder:text-white/30" />
                </div>
                <div>
                  <Label className="text-white/70">Message *</Label>
                  <Textarea data-testid="contact-message" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="How can we help?" rows={5} className="mt-2 border-white/15 bg-white/5 text-white placeholder:text-white/30" />
                </div>
                <motion.button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  whileTap={{ scale: 0.98 }}
                  className="flex items-center justify-center gap-2 rounded-xl bg-kbcyan py-4 font-display text-2xl tracking-wide text-black transition-transform hover:scale-[1.02] disabled:opacity-60"
                >
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                  {loading ? "Sending..." : "Send Message"}
                </motion.button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
